import { useMemo, useState, type CSSProperties } from 'react';
import {
  computeHealth,
  fmtCurrency,
  fmtRelative,
  isStalled,
  missingFields,
  stageMeta,
  type MissingField,
  type PipelineAccount,
  type StageKey,
} from '@/lib/pipelineAdapter';
import {
  Avatar,
  Chip,
  HealthRing,
  Logo,
  ScoreBar,
  SegRow,
  StageChipInline,
  ViewTabs,
} from './atoms';
import { BlockersCell, ChampCell, EBCell, LaunchCell, YnPill } from './cells';
import { SmartTableSidePanel } from './SidePanel';

interface SmartTableProps {
  accounts: PipelineAccount[];
  loading: boolean;
  /** Firebase UID of the signed-in analyst. Currently unused by filtering; kept for compatibility. */
  callerUid?: string | null;
  /** Caller's email — used to match against `Opportunity.OwnerId` -> SF user email for the "My deals" view. */
  callerEmail?: string | null;
  syncedRelative?: string | null;
}

type ViewKey = 'all' | 'mine' | 'risk' | 'closing';
type GroupKey = 'stage' | 'momentum' | 'owner' | 'none';

const VIEWS: { id: ViewKey; label: string }[] = [
  { id: 'all', label: 'All accounts' },
  { id: 'mine', label: 'My deals' },
  { id: 'risk', label: 'At risk' },
  { id: 'closing', label: 'Closing this quarter' },
];

const MISSING_CHIPS: MissingField[] = [
  'EB',
  'Champion',
  'Launch date',
  'Budget',
  'Legal',
  'Procurement',
];

const COL: Record<string, CSSProperties> = {
  account: { width: 220, flexShrink: 0 },
  health: {
    width: 60,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  signals: { width: 130, flexShrink: 0 },
  eb: { width: 200, flexShrink: 0 },
  champion: { width: 160, flexShrink: 0 },
  budget: { width: 80, flexShrink: 0 },
  launch: { width: 100, flexShrink: 0 },
  blockers: { flex: 1, minWidth: 200 },
  activity: { width: 90, flexShrink: 0 },
  owner: { width: 50, flexShrink: 0, display: 'flex', justifyContent: 'center' },
};

const HEADER_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
  borderBottom: '1px solid #e8ebf0',
  background: '#fafbfc',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  color: '#64748b',
  textTransform: 'uppercase',
};

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 14px',
  borderBottom: '1px solid #f1f5f9',
  transition: 'background .12s',
};

const BTN_S: CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  color: '#475569',
  border: '1px solid #e2e8f0',
  background: 'white',
  borderRadius: 6,
  cursor: 'pointer',
};

export function SmartTable({
  accounts,
  loading,
  callerEmail,
  syncedRelative,
}: SmartTableProps) {
  const [view, setView] = useState<ViewKey>('all');
  const [groupBy, setGroupBy] = useState<GroupKey>('stage');
  const [search, setSearch] = useState('');
  const [missingFilter, setMissingFilter] = useState<MissingField | null>(null);
  const [stalledOnly, setStalledOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [hoverBlocker, setHoverBlocker] = useState<string | null>(null);

  const callerEmailLower = callerEmail?.toLowerCase() ?? null;
  const matchesCaller = (a: PipelineAccount): boolean =>
    !!callerEmailLower && (a.owner.email ?? '').toLowerCase() === callerEmailLower;

  const filtered = useMemo(() => {
    let r = accounts;
    if (view === 'mine' && callerEmailLower) {
      r = r.filter(matchesCaller);
    } else if (view === 'risk') {
      r = r.filter((a) => computeHealth(a) < 50);
    } else if (view === 'closing') {
      r = r.filter((a) => a.stage === 'negotiation' || a.stage === 'closing');
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.economicBuyer.value ?? '').toLowerCase().includes(q) ||
          (a.champion.name ?? '').toLowerCase().includes(q),
      );
    }
    if (missingFilter) {
      r = r.filter((a) => missingFields(a).includes(missingFilter));
    }
    if (stalledOnly) {
      r = r.filter(isStalled);
    }
    return r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, view, search, missingFilter, stalledOnly, callerEmailLower]);

  const counts = useMemo(
    () => ({
      all: accounts.length,
      mine: callerEmailLower
        ? accounts.filter(matchesCaller).length
        : accounts.length,
      risk: accounts.filter((a) => computeHealth(a) < 50).length,
      closing: accounts.filter(
        (a) => a.stage === 'negotiation' || a.stage === 'closing',
      ).length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, callerEmailLower],
  );

  const grouped = useMemo<
    { key: string; label: string | null; color?: string; items: PipelineAccount[] }[]
  >(() => {
    if (groupBy === 'none') {
      return [{ key: 'all', label: null, items: filtered }];
    }
    if (groupBy === 'stage') {
      const order: StageKey[] = ['closing', 'negotiation', 'evaluation', 'discovery'];
      return order
        .map((s) => ({
          key: s,
          label: stageMeta[s].label,
          color: stageMeta[s].color,
          items: filtered.filter((a) => a.stage === s),
        }))
        .filter((g) => g.items.length);
    }
    if (groupBy === 'momentum') {
      return [
        {
          key: 'high',
          label: 'High momentum (4-5)',
          items: filtered.filter((a) => a.momentum >= 4),
        },
        {
          key: 'mid',
          label: 'Building (3)',
          items: filtered.filter((a) => a.momentum === 3),
        },
        {
          key: 'low',
          label: 'Low / stalled (1-2)',
          items: filtered.filter((a) => a.momentum <= 2),
        },
      ].filter((g) => g.items.length);
    }
    if (groupBy === 'owner') {
      const map = new Map<string, PipelineAccount[]>();
      filtered.forEach((a) => {
        const arr = map.get(a.owner.name) ?? [];
        arr.push(a);
        map.set(a.owner.name, arr);
      });
      return Array.from(map.entries()).map(([name, items]) => ({
        key: name,
        label: name,
        items,
      }));
    }
    return [{ key: 'all', label: null, items: filtered }];
  }, [filtered, groupBy]);

  const openAccount = accounts.find((a) => a.id === openId) ?? null;

  return (
    <div
      style={{
        position: 'relative',
        background: '#f7f8fa',
        borderRadius: 10,
        overflow: 'hidden',
        border: '1px solid #e8ebf0',
      }}
    >
      <div style={{ padding: '20px 28px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            marginBottom: 14,
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: '#0f172a',
                letterSpacing: -0.3,
              }}
            >
              Pipeline
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              {filtered.length} of {accounts.length} accounts
              {syncedRelative ? ` · synced ${syncedRelative}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search account, buyer, champion…"
              style={{
                width: 260,
                padding: '8px 12px 8px 32px',
                fontSize: 13,
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                background: `white url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'><circle cx='11' cy='11' r='7'/><path d='m20 20-3.5-3.5'/></svg>") no-repeat 11px 50%`,
                outline: 'none',
              }}
            />
            <button style={BTN_S} disabled>
              Export
            </button>
          </div>
        </div>

        <ViewTabs value={view} onChange={setView} views={VIEWS} count={counts} />
      </div>

      <div
        style={{
          padding: '12px 28px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          borderBottom: '1px solid #e8ebf0',
          background: 'white',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#94a3b8',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          Group by
        </span>
        <SegRow<GroupKey>
          value={groupBy}
          onChange={setGroupBy}
          opts={[
            ['stage', 'Stage'],
            ['momentum', 'Momentum'],
            ['owner', 'Owner'],
            ['none', 'None'],
          ]}
          compact
        />

        <span style={{ width: 1, height: 18, background: '#e2e8f0' }} />

        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#94a3b8',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          Missing
        </span>
        {MISSING_CHIPS.map((f) => (
          <button
            key={f}
            onClick={() => setMissingFilter(missingFilter === f ? null : f)}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 999,
              cursor: 'pointer',
              border: missingFilter === f ? '1px solid #3730a3' : '1px solid #e2e8f0',
              background: missingFilter === f ? '#eef2ff' : 'white',
              color: missingFilter === f ? '#3730a3' : '#475569',
            }}
          >
            {f}
          </button>
        ))}

        <span style={{ width: 1, height: 18, background: '#e2e8f0' }} />
        <label
          style={{
            fontSize: 12,
            color: '#475569',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={stalledOnly}
            onChange={(e) => setStalledOnly(e.target.checked)}
          />
          Stalled only (&gt;21d)
        </label>
      </div>

      <div style={{ padding: '0 28px 28px' }}>
        <div
          style={{
            background: 'white',
            border: '1px solid #e8ebf0',
            borderRadius: 10,
            overflowX: 'auto',
            overflowY: 'visible',
            marginTop: 14,
          }}
        >
        <div style={{ minWidth: 1180 }}>
          <div style={HEADER_ROW}>
            <div style={{ width: 36 }} />
            <div style={COL.account}>Account</div>
            <div style={COL.health}>Health</div>
            <div style={COL.signals}>Momentum · Strength</div>
            <div style={COL.eb}>Economic Buyer</div>
            <div style={COL.champion}>Champion</div>
            <div style={COL.budget}>Budget</div>
            <div style={COL.launch}>Launch</div>
            <div style={COL.blockers}>Blockers</div>
            <div style={COL.activity}>Activity</div>
            <div style={COL.owner}>Owner</div>
          </div>

          {loading && (
            <div style={{ padding: '40px 14px', color: '#94a3b8', fontSize: 13 }}>
              Loading accounts…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div
              style={{
                padding: '40px 14px',
                color: '#94a3b8',
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              No accounts match the current filters.
            </div>
          )}

          {!loading &&
            grouped.map((g) => (
              <div key={g.key}>
                {g.label && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 14px 8px',
                      background: '#fafbfc',
                      borderTop: '1px solid #e8ebf0',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: g.color ?? '#475569',
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                      }}
                    >
                      {g.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{g.items.length}</span>
                    <span style={{ flex: 1, height: 1, background: '#e8ebf0' }} />
                  </div>
                )}
                {g.items.map((a) => {
                  const score = computeHealth(a);
                  const stalled = isStalled(a);
                  return (
                    <div
                      key={a.id}
                      onClick={() => setOpenId(a.id)}
                      style={{
                        ...ROW,
                        cursor: 'pointer',
                        background: openId === a.id ? '#f5f3ff' : 'white',
                      }}
                      onMouseEnter={(e) => {
                        if (openId !== a.id) e.currentTarget.style.background = '#fafbfc';
                      }}
                      onMouseLeave={(e) => {
                        if (openId !== a.id) e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Logo color={a.logoColor} label={a.logo} size={26} radius={6} />
                      </div>
                      <div style={COL.account}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: '#0f172a',
                            }}
                          >
                            {a.name}
                          </span>
                          {stalled && (
                            <Chip bg="#fef2f2" color="#b91c1c" size="sm" dot="#ef4444">
                              stalled
                            </Chip>
                          )}
                        </div>
                        <div
                          style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}
                        >
                          {fmtCurrency(a.arr)} · <StageChipInline stage={a.stage} />
                        </div>
                      </div>
                      <div style={COL.health}>
                        <HealthRing score={score} size={28} stroke={3} />
                      </div>
                      <div style={COL.signals}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                          }}
                        >
                          <ScoreBar value={a.momentum} color="#3730a3" />
                          <ScoreBar value={a.strength} color="#0891b2" />
                        </div>
                      </div>
                      <div style={COL.eb}>
                        <EBCell eb={a.economicBuyer} signedOff={a.ebSignedOff} />
                      </div>
                      <div style={COL.champion}>
                        <ChampCell c={a.champion} />
                      </div>
                      <div style={COL.budget}>
                        <YnPill value={a.budgetApproved.value} />
                      </div>
                      <div style={COL.launch}>
                        <LaunchCell ld={a.launchDateAgreed} />
                      </div>
                      <div
                        style={COL.blockers}
                        onMouseEnter={() => setHoverBlocker(a.id)}
                        onMouseLeave={() => setHoverBlocker(null)}
                      >
                        <BlockersCell
                          blockers={a.blockers}
                          expand={hoverBlocker === a.id}
                        />
                      </div>
                      <div style={COL.activity}>
                        <span
                          style={{
                            fontSize: 12,
                            color: stalled ? '#b91c1c' : '#475569',
                          }}
                        >
                          {fmtRelative(a.lastActivity)}
                        </span>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {a.calls} calls{a.emails > 0 ? ` · ${a.emails} emails` : ''}
                        </div>
                      </div>
                      <div style={COL.owner}>
                        <Avatar
                          name={a.owner.name}
                          color={a.owner.color}
                          initials={a.owner.initials}
                          size={24}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
        </div>
      </div>

      <SmartTableSidePanel
        open={!!openId}
        onClose={() => setOpenId(null)}
        account={openAccount}
      />
    </div>
  );
}
